import React, {useState} from 'react';
import {Modal, StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchSourceCommands, sendSourceCommand} from '@/entities/stream/api';
import type {SourceCommand, SourceCommandName} from '@/entities/stream/types';
import {formatTimestamp} from '@/shared/lib/format';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {StatusPill, type StatusTone} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Commands to a camera, and what became of the last few.
 *
 * A command waits at most five minutes for the camera to pick it up and then expires
 * unrun — a reboot an hour late is a surprise, not a service. So the history is polled
 * while anything in it is still in flight, and stops once everything has an outcome.
 *
 * Reboot asks first: it drops every live picture from this camera for a minute.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  buttons: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(2)},
  history: {gap: spacing(2)},
  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  rowText: {flex: 1, gap: 2},
  backdrop: {flex: 1, justifyContent: 'center', padding: spacing(5)},
  dialog: {borderRadius: radii.xl, padding: spacing(5), gap: spacing(4)},
  dialogRow: {flexDirection: 'row', gap: spacing(2)},
  flex: {flex: 1},
});

const COMMANDS: SourceCommandName[] = ['reboot', 'sync_time', 'refresh_profiles'];

function toneFor(status: SourceCommand['status']): StatusTone {
  switch (status) {
    case 'done':
      return 'granted';
    case 'failed':
    case 'expired':
      return 'denied';
    case 'delivered':
      return 'online';
    default:
      return 'pending';
  }
}

const IN_FLIGHT = new Set(['queued', 'delivered']);

export function CameraCommandsPanel({
  companyId,
  sourceId,
}: {
  companyId: string;
  sourceId: string;
}) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();
  const [confirmReboot, setConfirmReboot] = useState(false);
  const key = ['source-commands', companyId, sourceId];

  const history = useQuery({
    queryKey: key,
    // Пульт PTZ пишет в ту же историю по команде на каждое нажатие — здесь видны
    // только команды этого экрана, иначе одно утро у поворотной камеры вытесняло бы
    // вчерашнюю перезагрузку.
    queryFn: () => fetchSourceCommands(companyId, sourceId, 50),
    select: data => ({
      data: data.data
        .filter(c => (COMMANDS as string[]).includes(c.command))
        .slice(0, 10),
    }),
    refetchInterval: q =>
      q.state.data?.data.some(c => IN_FLIGHT.has(c.status)) ? 2000 : false,
  });

  const send = useMutation({
    mutationFn: (command: SourceCommandName) =>
      sendSourceCommand(companyId, sourceId, command),
    onSuccess: () => {
      setConfirmReboot(false);
      void qc.invalidateQueries({queryKey: key});
    },
  });

  const commands = history.data?.data ?? [];

  return (
    <View style={styles.root}>
      <View style={styles.buttons}>
        {COMMANDS.map(command => (
          <AppButton
            key={command}
            title={t(`camera.command.${command}`)}
            tone={command === 'reboot' ? 'destructive' : 'outline'}
            loading={send.isPending && send.variables === command}
            onPress={() =>
              command === 'reboot' ? setConfirmReboot(true) : send.mutate(command)
            }
          />
        ))}
      </View>

      {send.isError ? (
        <Banner tone="error" title={messageOf(send.error, t('errors.unknown'))} />
      ) : null}

      {commands.length > 0 ? (
        <Card padding={4} style={styles.history}>
          {commands.map(c => (
            <View key={c.id} style={styles.row}>
              <View style={styles.rowText}>
                <AppText variant="subtitle" numberOfLines={1}>
                  {t(`camera.command.${c.command}`)}
                </AppText>
                <AppText variant="caption" numberOfLines={2}>
                  {formatTimestamp(c.createdAt)}
                  {typeof c.result?.error === 'string' ? ` · ${c.result.error}` : ''}
                </AppText>
              </View>
              <StatusPill
                tone={toneFor(c.status)}
                size="sm"
                label={t(`camera.commandStatus.${c.status}`)}
              />
            </View>
          ))}
        </Card>
      ) : null}

      <Modal
        visible={confirmReboot}
        transparent
        animationType="fade"
        onRequestClose={() => setConfirmReboot(false)}>
        <View style={[styles.backdrop, {backgroundColor: p.overlay}]}>
          <View style={[styles.dialog, {backgroundColor: p.elevated}]}>
            <AppText variant="title">{t('camera.rebootTitle')}</AppText>
            <AppText variant="body" color={p.mutedForeground}>
              {t('camera.rebootBody')}
            </AppText>
            <View style={styles.dialogRow}>
              <AppButton
                title={t('actions.cancel')}
                tone="outline"
                style={styles.flex}
                onPress={() => setConfirmReboot(false)}
              />
              <AppButton
                title={t('camera.command.reboot')}
                tone="destructive"
                style={styles.flex}
                loading={send.isPending}
                onPress={() => send.mutate('reboot')}
              />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}
